"use client";
import { API_ENDPOINTS } from "../../../../constants/config";
import { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../../utils/axiosInstance";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
  Edit,
  Trash,
  Shield,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

function getFullName(u) {
  if (!u) return "";
  return [u.firstName, u.middleName, u.lastName].filter(Boolean).join(" ") || u.username || "";
}

function AddAdminFromStaff({ onClose, onSuccess, existingAdminUserIds = [] }) {
  const [staffList, setStaffList] = useState([]);
  const [rolesList, setRolesList] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingStaff, setLoadingStaff] = useState(true);
  const [loadingRoles, setLoadingRoles] = useState(true);

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        setLoadingStaff(true);
        const token = localStorage.getItem("token");
        const res = await axiosInstance.get(API_ENDPOINTS.users, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const users = res.data?.data?.users ?? res.data?.users ?? [];
        const staff = users.filter((u) => u.role === "staff");
        setStaffList(staff);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load staff");
      } finally {
        setLoadingStaff(false);
      }
    };
    fetchStaff();
  }, []);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setLoadingRoles(true);
        const res = await axiosInstance.get(API_ENDPOINTS.schoolRoles);
        setRolesList(res.data?.data?.roles ?? []);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load roles");
      } finally {
        setLoadingRoles(false);
      }
    };
    fetchRoles();
  }, []);

  const staffOptions = staffList.filter(
    (s) => !existingAdminUserIds.includes(s._id)
  );

  const handleSubmit = async () => {
    if (!selectedStaffId) {
      setError("Please select a staff member");
      return;
    }
    if (!selectedRoleId) {
      setError("Please select a role. Create roles in the Roles tab first.");
      return;
    }
    try {
      setError(null);
      setLoading(true);
      const token = localStorage.getItem("token");
      await axiosInstance.post(
        API_ENDPOINTS.adminAccessCreate,
        { staffId: selectedStaffId, roleId: selectedRoleId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to add access");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardContent className="p-6">
        <h2 className="text-xl font-semibold mb-4">Give staff access</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Select a staff member and a role. Permissions come from the role (define roles in the Roles tab).
        </p>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <div className="space-y-4">
          <div>
            <Label>Select Staff *</Label>
            <Select
              value={selectedStaffId}
              onValueChange={setSelectedStaffId}
              disabled={loadingStaff}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select staff member..." />
              </SelectTrigger>
              <SelectContent>
                {staffOptions.length === 0 ? (
                  <SelectItem value="_none" disabled>
                    {loadingStaff ? "Loading..." : "No staff available or all already have access"}
                  </SelectItem>
                ) : (
                  staffOptions.map((s) => (
                    <SelectItem key={s._id} value={s._id}>
                      {getFullName(s)} {s.username ? `(@${s.username})` : ""}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Role *</Label>
            <Select value={selectedRoleId} onValueChange={setSelectedRoleId} disabled={loadingRoles}>
              <SelectTrigger>
                <SelectValue placeholder="Select role..." />
              </SelectTrigger>
              <SelectContent>
                {rolesList.length === 0 ? (
                  <SelectItem value="_none" disabled>
                    {loadingRoles ? "Loading..." : "No roles. Create roles in the Roles tab first."}
                  </SelectItem>
                ) : (
                  rolesList.map((r) => (
                    <SelectItem key={r._id} value={r._id}>
                      {r.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex justify-end space-x-2 mt-4">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || !selectedStaffId || !selectedRoleId || loadingStaff || loadingRoles}
          >
            {loading ? "Saving..." : "Give access"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function EditAdmin({ adminData, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: adminData.name,
    email: adminData.email,
  });
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    try {
      setError(null);
      const token = localStorage.getItem("token");
      await axiosInstance.put(
        API_ENDPOINTS.adminAccessById(adminData._id),
        { name: formData.name, email: formData.email },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      onSuccess();
    } catch (error) {
      setError(error.response?.data?.message || "Failed to update admin");
      console.error("Error updating admin:", error);
    }
  };

  return (
    <Card>
      <CardContent className="p-6">
        <h2 className="text-xl font-semibold mb-4">Edit Admin</h2>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <div className="space-y-4">
          <div>
            <Label htmlFor="name">Full Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
            />
          </div>

          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
            />
          </div>
        </div>

        <div className="flex justify-end space-x-2 mt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit}>Update Admin</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function EditPermissions({ adminId, adminName, onClose, onSuccess }) {
  const [roleId, setRoleId] = useState("");
  const [rolesList, setRolesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [permRes, rolesRes] = await Promise.all([
          axiosInstance.get(API_ENDPOINTS.adminAccessPermissions(adminId)),
          axiosInstance.get(API_ENDPOINTS.schoolRoles),
        ]);
        setRoleId(permRes.data.data.roleId || "");
        setRolesList(rolesRes.data?.data?.roles ?? []);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [adminId]);

  const handleSave = async () => {
    if (!roleId) {
      setError("Please select a role");
      return;
    }
    try {
      setSaving(true);
      setError(null);
      await axiosInstance.put(API_ENDPOINTS.adminAccessPermissions(adminId), { roleId });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update role");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardContent className="p-6">
        <h2 className="text-xl font-semibold mb-4">Edit role for {adminName}</h2>
        <p className="text-sm text-muted-foreground mb-4">Changing the role updates their permissions to match that role.</p>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {loading ? (
          <div className="flex justify-center my-4">Loading...</div>
        ) : (
          <div>
            <Label>Role</Label>
            <Select value={roleId || "_none"} onValueChange={(v) => setRoleId(v === "_none" ? "" : v)}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select role..." />
              </SelectTrigger>
              <SelectContent>
                {rolesList.length === 0 ? (
                  <SelectItem value="_none" disabled>No roles. Create roles in the Roles tab.</SelectItem>
                ) : (
                  rolesList.map((r) => (
                    <SelectItem key={r._id} value={r._id}>{r.name}</SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="flex justify-end space-x-2 mt-4">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || loading || !roleId}>
            {saving ? "Saving..." : "Save role"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function DeleteConfirm({ adminData, onClose, onConfirm }) {
  return (
    <Card>
      <CardContent className="p-6">
        <h2 className="text-xl font-semibold mb-4">Delete Admin</h2>
        <p>Are you sure you want to delete {adminData.name}?</p>
        <p className="text-gray-500 mt-2">This action cannot be undone.</p>
        <div className="flex justify-end space-x-2 mt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm}>
            Delete
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function AdminList() {
  const [admins, setAdmins] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [error, setError] = useState(null);
  const [modalType, setModalType] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${token}`;
    }
  }, []);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field) => {
    if (field !== sortField) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400 ml-1" />;
    }
    return sortDirection === "asc" ? (
      <ChevronUp className="h-4 w-4 text-[#0263cc] ml-1" />
    ) : (
      <ChevronDown className="h-4 w-4 text-[#0263cc] ml-1" />
    );
  };

  const getSortedAdmins = () => {
    if (!sortField) return admins;

    return [...admins].sort((a, b) => {
      let valueA = a[sortField];
      let valueB = b[sortField];

      // String comparison for text fields
      if (typeof valueA === "string" && typeof valueB === "string") {
        valueA = valueA.toLowerCase();
        valueB = valueB.toLowerCase();
      }

      if (valueA < valueB) return sortDirection === "asc" ? -1 : 1;
      if (valueA > valueB) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  };

  const fetchAdmins = useCallback(async () => {
    try {
      setError(null);
      const response = await axiosInstance.get(API_ENDPOINTS.adminAccess);
      if (response.data && response.data.data) {
        setAdmins(
          response.data.data.admins || response.data.data.adminAccess || []
        );
      } else {
        console.error("Unexpected API response format:", response.data);
        setAdmins([]);
      }
    } catch (error) {
      setError(error.response?.data?.message || "Failed to fetch admins");
      console.error("Error fetching admins:", error);
      setAdmins([]);
    }
  }, []);

  const handleDelete = async (id) => {
    try {
      setError(null);
      await axiosInstance.delete(API_ENDPOINTS.adminAccessById(id));
      fetchAdmins();
      setShowModal(false);
    } catch (error) {
      setError(error.response?.data?.message || "Failed to delete admin");
      console.error("Error deleting admin:", error);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const handleSuccess = () => {
    setShowModal(false);
    setSelectedAdmin(null);
    fetchAdmins();
  };

  const openModal = (type, admin) => {
    setSelectedAdmin(admin);
    setModalType(type);
    setShowModal(true);
  };

  return (
    <Card className="w-full">
      <CardContent className="p-6">
        <div className="flex justify-between items-center mb-6">
          <Button variant="outline" onClick={fetchAdmins}>
            Refresh List
          </Button>
          <Button onClick={() => openModal("add", null)}>Give staff access</Button>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead
                className="font-semibold text-gray-600 cursor-pointer"
                onClick={() => handleSort("name")}
              >
                <div className="flex items-center">
                  Name
                  {getSortIcon("name")}
                </div>
              </TableHead>
              <TableHead
                className="font-semibold text-gray-600 cursor-pointer"
                onClick={() => handleSort("username")}
              >
                <div className="flex items-center">
                  Username
                  {getSortIcon("username")}
                </div>
              </TableHead>
              <TableHead
                className="font-semibold text-gray-600 cursor-pointer"
                onClick={() => handleSort("email")}
              >
                <div className="flex items-center">
                  Email
                  {getSortIcon("email")}
                </div>
              </TableHead>
              <TableHead>Role</TableHead>
              <TableHead className="text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {getSortedAdmins().map((admin) => (
              <TableRow key={admin._id}>
                <TableCell>{admin.name}</TableCell>
                <TableCell>{admin.username ? `@${admin.username}` : "–"}</TableCell>
                <TableCell>{admin.email}</TableCell>
                <TableCell>{admin.roleType || "–"}</TableCell>
                <TableCell>
                  <div className="flex justify-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 px-2"
                      onClick={() => openModal("edit", admin)}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 px-2"
                      onClick={() => openModal("permissions", admin)}
                    >
                      <Shield className="h-4 w-4 mr-1" />
                      Permissions
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 px-2 text-red-500 hover:bg-red-50 hover:text-red-600"
                      onClick={() => openModal("delete", admin)}
                    >
                      <Trash className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {showModal && (
          <Dialog open={showModal} onOpenChange={setShowModal}>
            <DialogContent>
              {modalType === "add" && (
                <AddAdminFromStaff
                  onClose={() => setShowModal(false)}
                  onSuccess={handleSuccess}
                  existingAdminUserIds={admins.map((a) => a._id)}
                />
              )}

              {modalType === "edit" && selectedAdmin && (
                <EditAdmin
                  adminData={selectedAdmin}
                  onClose={() => setShowModal(false)}
                  onSuccess={handleSuccess}
                />
              )}

              {modalType === "permissions" && selectedAdmin && (
                <EditPermissions
                  adminId={selectedAdmin._id}
                  adminName={selectedAdmin.name}
                  onClose={() => setShowModal(false)}
                  onSuccess={handleSuccess}
                />
              )}

              {modalType === "delete" && selectedAdmin && (
                <DeleteConfirm
                  adminData={selectedAdmin}
                  onClose={() => setShowModal(false)}
                  onConfirm={() => handleDelete(selectedAdmin._id)}
                />
              )}
            </DialogContent>
          </Dialog>
        )}
      </CardContent>
    </Card>
  );
}
